/**
 * @voltxampere/web — Voltage heatmap overlay (Sprint 30-3)
 *
 * Paints per-node radial gradient halos onto the fx canvas (the same
 * overlay <canvas> the Sprint 29 meteor particles use). Composite mode:
 * `screen` (additive) so brass-hot stays visible against the void
 * background and overlapping halos brighten naturally.
 *
 * Mockup §E canonical (Şefim 2026-05-02 notu, brand spec türevi):
 *   vfHigh #E8B35C 0.35 (V > 67%)
 *   vfMid  #6082A8 0.25 (V 33-67%)
 *   vfLow  #283C64 0.20 (V < 33%)
 *
 * Each halo radius = lerp(MIN_RADIUS, MAX_RADIUS, |V_normalised|) world
 * pixels — high-magnitude nodes glow wider so the eye reads "more
 * energy here". Three-stop gradient: token-color × token-opacity at 0%,
 * fully transparent at 100%.
 *
 * The renderer reads `currentVoltageSamples` (apps/web/src/store/fx-state.ts)
 * which already filters out ground nets and produces `NodeVoltageSample[]`
 * with world coordinates. This module only paints — derivation lives in
 * the store layer.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { voltageRange, voltageToColor, type NodeVoltageSample, type RGBA } from "@voltxampere/fx";

/** World-pixel halo radius bounds. */
const MIN_RADIUS = 80;
const MAX_RADIUS = 120;

export interface HeatmapDrawOpts {
  readonly viewport: {
    readonly center: { x: number; y: number };
    readonly zoom: number;
  };
  readonly cssWidth: number;
  readonly cssHeight: number;
  readonly dpr: number;
  /** Node voltage samples — derived from currentTransientResult.timeSeries.last. */
  readonly samples: readonly NodeVoltageSample[];
}

/**
 * Paint per-node radial gradient halos onto the heatmap canvas.
 *
 * Caller is responsible for clearing the canvas + setting up the world
 * transform (mirrors the meteor renderer in fx-overlay.ts). This helper
 * only owns the per-sample gradient draw + composite mode toggle.
 *
 * Empty samples → no-op (caller already cleared the canvas, no halos to
 * paint). Degenerate range (every node at the same voltage, e.g. cold
 * boot before any transient) → uses vfLow as the fallback color so a
 * faint blue field still hints at "current circuit, no activity yet".
 */
export function drawVoltageHeatmap(ctx: CanvasRenderingContext2D, opts: HeatmapDrawOpts): void {
  if (opts.samples.length === 0) return;

  ctx.save();
  ctx.setTransform(opts.dpr, 0, 0, opts.dpr, 0, 0);
  // World transform — mirrors the schematic renderer + meteor overlay.
  const z = opts.viewport.zoom;
  ctx.translate(opts.cssWidth / 2, opts.cssHeight / 2);
  ctx.scale(z, z);
  ctx.translate(-opts.viewport.center.x, -opts.viewport.center.y);

  // Additive composite so overlapping halos brighten naturally and brass-
  // hot reads as warm peak against the void backdrop.
  ctx.globalCompositeOperation = "screen";

  const range = voltageRange(opts.samples);

  for (const sample of opts.samples) {
    paintHalo(ctx, sample, range);
  }

  // Restore source-over for any subsequent draws (meteor particles run
  // in their own ctx.save/restore but defensive reset is cheap).
  ctx.globalCompositeOperation = "source-over";
  ctx.restore();
}

/**
 * Paint a single node's radial halo. Three-stop gradient:
 *   stop 0%   — palette color × token opacity
 *   stop 70%  — palette color × (token opacity × 0.4) — soft falloff
 *   stop 100% — fully transparent
 *
 * The 70% inflection point keeps the halo's bright core compact while
 * the outer edge bleeds smoothly into adjacent halos via screen blend.
 */
function paintHalo(
  ctx: CanvasRenderingContext2D,
  sample: NodeVoltageSample,
  range: { min: number; max: number },
): void {
  const radius = computeRadius(sample.voltage, range);
  const color = voltageToColor(sample.voltage, range);
  const grad = ctx.createRadialGradient(
    sample.position.x,
    sample.position.y,
    0,
    sample.position.x,
    sample.position.y,
    radius,
  );
  grad.addColorStop(0, rgbaString(color, color.a));
  grad.addColorStop(0.7, rgbaString(color, color.a * 0.4));
  grad.addColorStop(1, rgbaString(color, 0));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(sample.position.x, sample.position.y, radius, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Halo radius scales with voltage magnitude relative to the range
 * extremes. Edge cases:
 *  - degenerate range (V_max === V_min) → MIN_RADIUS (compact halo)
 *  - voltages outside the range → clamped to [MIN_RADIUS, MAX_RADIUS]
 */
function computeRadius(voltage: number, range: { min: number; max: number }): number {
  const span = range.max - range.min;
  if (span === 0) return MIN_RADIUS;
  // Normalise to [0, 1] using the absolute deviation from the band's
  // midpoint — high magnitude (positive OR negative) wins the wider
  // halo, matching the user's visual expectation that "extremes glow
  // brightest".
  const mid = (range.max + range.min) / 2;
  const halfSpan = span / 2;
  const deviation = Math.abs(voltage - mid) / halfSpan;
  const clamped = Math.max(0, Math.min(1, deviation));
  return MIN_RADIUS + (MAX_RADIUS - MIN_RADIUS) * clamped;
}

/** Format a renderer-friendly RGBA into the canvas `rgba(...)` string. */
function rgbaString(color: RGBA, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${String(color.r)},${String(color.g)},${String(color.b)},${String(a)})`;
}
